'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { CellTokenAmountPrice, MarketCell } from '@morpho-org/ui-v6/client'
import { Skeleton, Tag } from '@morpho-org/ui-v6/server'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { abbreviateAddress } from '@repo/utils'
import { rowPaginationFeature, rowSortingFeature, tableFeatures } from '@tanstack/react-table'

import { ButtonLink } from '@/components/common/ButtonLink'
import { TimestampCell } from '@/components/common/Cells/TimestampCell'
import { InternalLink } from '@/components/common/InternalLink'
import { WarningIcon } from '@/components/common/WarningIcon'

import type { VaultReallocationTableRow } from './VaultReallocation.types'

export const vaultReallocationTableFeatures = tableFeatures({
  rowPaginationFeature,
  rowSortingFeature
})
export type VaultReallocationTableFeatures = typeof vaultReallocationTableFeatures

export const vaultAllocationColumnsDefs: ColumnDefWithSkeleton<
  VaultReallocationTableFeatures,
  VaultReallocationTableRow
>[] = [
  {
    accessorKey: 'timestamp',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Date</span>
        </TooltipTrigger>
        <TooltipContent>Timestamp of when the transaction occurred.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      return <TimestampCell timestamp={row.original.timestamp} />
    },
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[122px] rounded-[20px]" />
  },
  {
    accessorKey: 'caller',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            Allocator
          </span>
        </TooltipTrigger>
        <TooltipContent>User who executed the reallocate.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-[2px]">
          <ButtonLink
            href={row.original.urlCaller}
            mode="external"
            buttonProps={{
              variant: 'ghost',
              size: 'default',
              label: abbreviateAddress(row.original.caller),
              avatarProps: {
                address: row.original.caller
              }
            }}
          />
          {row.original.isPublicAllocator && <Tag variant="main" label="Public Allocator" />}
        </div>
      )
    },
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[143px] rounded-[20px]" />
  },
  {
    accessorKey: 'assets',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Amount</span>
        </TooltipTrigger>
        <TooltipContent>The amount of token involved in the transaction.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenAmountPrice
        token={row.original.token}
        amount={row.original.assets}
        value={row.original.amountValue}
        withAvatar
        sign
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[190px] rounded-[20px]" />
  },
  {
    accessorKey: 'market',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            Market
          </span>
        </TooltipTrigger>
        <TooltipContent>The market concerned by the reallocate.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const marketCell = (
        <MarketCell
          collateralAsset={row.original.market.collateralAsset}
          loanAsset={row.original.market.loanAsset}
          lltv={row.original.market.lltv}
          chainId={row.original.token.chain.id}
          renderWarnings={<WarningIcon warnings={row.original.market.warnings} />}
        />
      )

      if (row.original.marketUrl) {
        return (
          <InternalLink href={row.original.marketUrl} withHoverEffect>
            {marketCell}
          </InternalLink>
        )
      }

      // Idle-liquidity markets have no market page, so they skip the InternalLink
      // wrapper. Mirror its `withHoverEffect` left inset (pl-[7px]) so non-linked
      // rows stay horizontally aligned with the linked ones.
      return <div className="pl-[7px]">{marketCell}</div>
    },
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[240px] rounded-[20px]" />
  },
  {
    accessorKey: 'txHash',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '10px' }}>
            Hash
          </span>
        </TooltipTrigger>
        <TooltipContent>The transaction hash.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <ButtonLink
        href={row.original.urlHash}
        mode="external"
        buttonProps={{
          variant: 'ghost',
          size: 'default',
          label: abbreviateAddress(row.original.txHash)
        }}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[122px] rounded-[20px]" />
  }
]
